/**
 * A tiny, dependency-free markdown parser for the constrained subset we ship in first-party
 * copy (desktop release notes, in-app changelog sections): headings, ordered/unordered lists,
 * paragraphs, and inline bold / italic / code / links. It is deliberately NOT a full CommonMark
 * implementation — no tables, blockquotes, code fences, images, or raw HTML. Output is a plain
 * data tree the renderer turns into safe semantic elements (no `dangerouslySetInnerHTML`).
 */

/** A parsed top-level block. */
export type TBlock =
  | { type: "heading"; level: number; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "paragraph"; text: string }

/** A parsed inline span. `code` is a leaf (its content is never re-parsed). */
export type TInlineToken =
  | { type: "text"; value: string }
  | { type: "code"; value: string }
  | { type: "bold"; children: TInlineToken[] }
  | { type: "italic"; children: TInlineToken[] }
  | { type: "link"; href: string; children: TInlineToken[] }

const HEADING_RE = /^(#{1,6})\s+(.*)$/
const UL_RE = /^\s*[-*+]\s+/
const OL_RE = /^\s*\d+\.\s+/

const isBlockStart = (line: string): boolean => HEADING_RE.test(line) || UL_RE.test(line) || OL_RE.test(line)

type TInlineRule = { type: "code" | "bold" | "italic" | "link"; re: RegExp }

// Order matters only as a tie-breaker — the scanner always prefers the earliest match, and bold
// is listed before italic so `**x**` is never mistaken for italics at the same index.
const INLINE_RULES: TInlineRule[] = [
  { type: "code", re: /`([^`]+)`/ },
  { type: "bold", re: /\*\*([\s\S]+?)\*\*/ },
  { type: "italic", re: /\*([^*\n]+?)\*|_([^_\n]+?)_/ },
  { type: "link", re: /\[([^\]]+)\]\(([^)\s]+)\)/ },
]

/**
 * Split markdown into top-level blocks. Wrapped list items (lazy continuation lines that are
 * indented under a bullet) are folded back into their item, and soft-wrapped paragraph lines are
 * joined with a space — matching how the changelog is authored.
 */
export const parseBlocks = (markdown: string): TBlock[] => {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n")
  const blocks: TBlock[] = []
  let i = 0

  const collectList = (re: RegExp): string[] => {
    const items: string[] = []
    while (i < lines.length) {
      const line = lines[i]
      if (re.test(line)) {
        items.push(line.replace(re, "").trim())
        i++
      } else if (line.trim() !== "" && !isBlockStart(line) && items.length > 0) {
        // Lazy continuation: a wrapped line belongs to the preceding item.
        items[items.length - 1] = `${items[items.length - 1]} ${line.trim()}`
        i++
      } else {
        break
      }
    }
    return items
  }

  while (i < lines.length) {
    const line = lines[i]

    if (line.trim() === "") {
      i++
      continue
    }

    const heading = HEADING_RE.exec(line)
    if (heading) {
      blocks.push({ type: "heading", level: heading[1].length, text: heading[2].trim() })
      i++
      continue
    }

    if (UL_RE.test(line)) {
      blocks.push({ type: "ul", items: collectList(UL_RE) })
      continue
    }

    if (OL_RE.test(line)) {
      blocks.push({ type: "ol", items: collectList(OL_RE) })
      continue
    }

    const paragraph: string[] = []
    while (i < lines.length && lines[i].trim() !== "" && !isBlockStart(lines[i])) {
      paragraph.push(lines[i].trim())
      i++
    }
    blocks.push({ type: "paragraph", text: paragraph.join(" ") })
  }

  return blocks
}

/**
 * Tokenise a single line of inline markdown. The scanner repeatedly finds the earliest matching
 * rule and recurses into the captured content (except inline code, which is emitted verbatim), so
 * emphasis can nest — e.g. `**bold _and italic_**`.
 */
export const parseInline = (text: string): TInlineToken[] => {
  const tokens: TInlineToken[] = []
  let rest = text

  while (rest.length > 0) {
    let best: { index: number; rule: TInlineRule; match: RegExpExecArray } | null = null
    for (const rule of INLINE_RULES) {
      const match = rule.re.exec(rest)
      if (match && (best === null || match.index < best.index)) {
        best = { index: match.index, rule, match }
      }
    }

    if (best === null) {
      tokens.push({ type: "text", value: rest })
      break
    }

    if (best.index > 0) {
      tokens.push({ type: "text", value: rest.slice(0, best.index) })
    }

    const { match, rule } = best
    if (rule.type === "code") {
      tokens.push({ type: "code", value: match[1] })
    } else if (rule.type === "bold") {
      tokens.push({ type: "bold", children: parseInline(match[1]) })
    } else if (rule.type === "italic") {
      tokens.push({ type: "italic", children: parseInline(match[1] ?? match[2]) })
    } else {
      tokens.push({ type: "link", href: match[2], children: parseInline(match[1]) })
    }

    rest = rest.slice(best.index + match[0].length)
  }

  return tokens
}

const ALLOWED_LINK_SCHEMES = new Set(["http", "https", "mailto"])

/**
 * SECURITY — sanitize a markdown link URL before it becomes an `<a href>`.
 *
 * This markdown renders AI-generated content, which can contain a link whose URL
 * is `javascript:…` / `data:…` / `vbscript:…` — an XSS vector when placed in an
 * href. Only http(s), mailto, and relative/anchor links are allowed; anything else
 * collapses to "#". The scheme is probed on a copy with whitespace + control chars
 * stripped, so obfuscations like `java\tscript:` (which browsers still execute)
 * can't slip past.
 */
export function safeHref(href: string): string {
  const trimmed = href.trim()
  // biome-ignore lint/suspicious/noControlCharactersInRegex: intentional - stripping control chars defeats scheme obfuscation like java<tab>script:
  const probe = trimmed.replace(/[\u0000-\u0020\u007f-\u00a0]/g, "").toLowerCase()
  const scheme = /^([a-z][a-z0-9+.-]*):/.exec(probe)
  if (!scheme) return trimmed // relative / anchor — no scheme, safe
  return ALLOWED_LINK_SCHEMES.has(scheme[1]) ? trimmed : "#"
}
